/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { bool, func } from 'prop-types';
import {
  Modal,
  AnchorTextLink,
  Tooltip,
  Text,
  TextLink,
  Stack,
  Box,
  Button,
  Position,
  ButtonGroup,
  Input,
} from '@chaser/components';
import { useDispatch } from 'react-redux';

import { Formik, Form, Field } from 'formik';
import usePush from '../../../../hooks/usePush';
import AuthenticationService from '../../../../services/authentication-service';
import { useHistoryState } from '../../../../hooks/useGlobalState';
import LoadingMessage from '../../../../components/LoadingMessage';
import TotpSetupForm from './TotpSetupForm.jsx';

import successImg from './success.svg';

import useSteps from '../../../../hooks/useSteps';
import { SET_USER_TOKEN } from '../../../login/duck/types';
import useUserInfo from '../../../../hooks/useUserInfo';

const Step1 = ({ stepProps: { nextStep }, refetch }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const { execute: executeValidate, loading: loadingValidate } = usePush(
    AuthenticationService.validateTotpSetup,
    {
      errorToast: 'Invalid code',
    },
  );

  async function onSubmit(values, actions) {
    const [err] = await executeValidate(values.code.replace(/\s/g, ''));
    actions.setSubmitting(false);
    if (err) return;
    nextStep();
    refetch();
  }

  return (
    <TotpSetupForm
      bodyAs={Modal.Body}
      onSubmit={onSubmit}
      isLoading={setIsLoading}
      isComplete={setIsComplete}
      buttons={
        <Modal.Footer>
          <Text color="Mid Neutral 1">
            <AnchorTextLink href="https://help.chaserhq.com/2-step-authentication" monochrome>
              Learn more about 2-Factor Authentication
            </AnchorTextLink>
          </Text>
          <Button disabled={isLoading || !isComplete} loading={loadingValidate} type="submit">
            Enable 2-Factor Authentication
          </Button>
        </Modal.Footer>
      }
    />
  );
};

const RemoveModal = ({ open, toggle, refetch }) => {
  const [view, setView] = useState('totp');
  const dispatch = useDispatch();
  const { execute: executeRemoveTotp, loading: removeLoading, error: removeError } = usePush(
    async (...args) => {
      const { token } = await AuthenticationService.removeTotp(...args);
      dispatch({ type: SET_USER_TOKEN, token });
      await refetch();
    },
    {
      errorToast: true,
      successToast: '2-Step Authentication succesfully removed',
    },
  );
  const { execute: executeSendMail, loading: sendingMail } = usePush(
    AuthenticationService.requestRemoveEmail,
  );

  useEffect(() => {
    if (view === 'totpEmail') {
      executeSendMail();
    }
  }, [executeSendMail, view]);

  return (
    <Modal size="small" open={open} toggle={toggle} title="Remove 2FA">
      <Formik
        initialValues={{
          _reinit: view,
          code: '',
        }}
        enableReinitialize
        onSubmit={async (values, actions) => {
          const [err] = await executeRemoveTotp(values.code, view);
          actions.setSubmitting(false);
          if (err) return;
          toggle(false);
        }}
      >
        {({ values }) => (
          <Form>
            <Modal.Body>
              <Stack gap="medium">
                <Stack>
                  {sendingMail ? (
                    <LoadingMessage />
                  ) : (
                    <>
                      <Text>
                        {view === 'totp'
                          ? 'Enter 6-digit code shown on your authentication app'
                          : 'Enter 6-digit code sent to your registered email'}
                      </Text>
                      <Field
                        as={Input}
                        icon="lock"
                        name="code"
                        placeholder={
                          view === 'totp'
                            ? 'Enter code from authentication app'
                            : 'Enter code sent to email'
                        }
                        width="full"
                        autoFocus
                        error={removeError}
                      />
                    </>
                  )}
                </Stack>
                <Text>
                  <TextLink
                    as="button"
                    type="button"
                    onClick={() => setView(view === 'totp' ? 'totpEmail' : 'totp')}
                  >
                    {view === 'totp'
                      ? 'Can’t access the authentication app?'
                      : 'Use authentication app'}
                  </TextLink>
                </Text>
              </Stack>
            </Modal.Body>
            <Modal.Footer>
              <Button disabled={!values.code} loading={removeLoading} intent="danger" type="submit">
                Remove Two-Factor Authentication
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

const Totp = ({ enabled, refetch }) => {
  const [{ totpSetupShown = false }, setState] = useHistoryState();
  const [totpRemoveShown, setTotpRemoveShown] = useState(false);
  const user = useUserInfo();

  function setTotpSetupShown(value) {
    setState({ totpSetupShown: value });
  }

  const [stepElement, { gotoStep, step }] = useSteps([
    <Step1 refetch={refetch} />,
    () => (
      <>
        <Modal.Body>
          <Stack gap="medium">
            <Text>
              From now on, when you sign in to Chaser, you will need your password and an
              authentication code from your authenticator app.
            </Text>
            <Box p="small" display="flex" alignItems="center" justifyContent="center">
              <img src={successImg} alt="" />
            </Box>
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setTotpSetupShown(false);
            }}
            autoFocus
          >
            Finish
          </Button>
        </Modal.Footer>
      </>
    ),
  ]);

  const title = ['Set up 2FA', '2-Step Authentication active!'][step - 1];

  return (
    <>
      <Modal
        open={totpSetupShown}
        toggle={val => {
          setTotpSetupShown(val);
        }}
        title={title}
      >
        {stepElement}
      </Modal>
      <RemoveModal open={totpRemoveShown} toggle={setTotpRemoveShown} refetch={refetch} />
      <Stack>
        {enabled ? (
          <ButtonGroup>
            <Button
              appearance="secondary"
              intent="danger"
              onClick={() => {
                setTotpRemoveShown(true);
              }}
            >
              Remove
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup>
            <Tooltip
              disable={user.hasVerifiedEmail}
              content="Please verify your email before setting up 2FA"
              position={Position.TOP}
            >
              <Button
                appearance="secondary"
                onClick={() => {
                  gotoStep(1);
                  setTotpSetupShown(true);
                }}
                disabled={!user.hasVerifiedEmail}
              >
                Set up 2FA
              </Button>
            </Tooltip>
          </ButtonGroup>
        )}
      </Stack>
    </>
  );
};

Totp.propTypes = {
  enabled: bool,
  refetch: func.isRequired,
};

export default Totp;
